import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Paginator } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';
import { Filter, LayoutGrid, List } from 'lucide-react';
import { Avatar } from 'primereact/avatar';
import { useNavigate } from 'react-router-dom';
import { getInitials } from 'utils/getInitials';
import { getBackgroundColor } from 'utils/avatarUtilities';
import BidCard from './components/BidCard';
import BidListView from './components/BidListView';
import NotesViewModal from './components/NotesViewModal';
import useManagerReviewLogic from './ManagerReview.logic';
import styles from './ManagerReview.module.scss';
import { useStore } from 'hooks/useStore';

const ManagerReview: React.FC = () => {
  const navigate = useNavigate();
  const {
    UserStore: { userMetadata },
  } = useStore();

  const {
    bids,
    loading,
    totalRecords,
    pageSize,
    currentPage: _currentPage,
    activeTab,
    viewMode,
    isFilterVisible: _isFilterVisible,
    selectedFilters,
    appliedFilters,
    allAnalysts,
    allProductTypes,
    approvedBids,
    approvedBidsLoading,
    approvedBidsTotalRecords,
    approvedBidsCurrentPage: _approvedBidsCurrentPage,
    selectedApprovedBidsFilters,
    appliedApprovedBidsFilters,
    approvedBidsAllAnalysts,
    approvedBidsAllCreatedBy: _approvedBidsAllCreatedBy,
    isLoadingApprovedBidsAnalysts,
    isLoadingAnalysts,
    selectedBidForNotes,
    isNotesModalVisible,
    userAttributes,
    isLoadingAttributes,
    handleTabChange,
    handleViewModeToggle,
    handleFilterToggle: _handleFilterToggle,
    handleFilterChange,
    handleApplyFilters,
    handlePageChange,
    handleViewNotes,
    handleCloseNotesModal,
    getFirstRecord,
    handleApprovedBidsFilterChange,
    handleApprovedBidsApplyFilters,
    handleApprovedBidsPageChange,
    getApprovedBidsFirstRecord,
  } = useManagerReviewLogic();

  const statusOptions = [
    { label: 'Pending Review', value: 'In Review' },
    { label: 'Needs Modification', value: 'Needs modification' },
  ];

  const renderLoadingSkeleton = () => {
    if (viewMode === 'cards') {
      return (
        <div className={styles.cardsGrid}>
          {[...Array(6)].map((_, index) => (
            <div key={index} className={styles.skeletonCard}>
              <Skeleton height="350px" borderRadius="8px" />
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className={styles.skeletonList}>
          <Skeleton height="3rem" width="100%" style={{ marginBottom: '1rem' }} />
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} height="4rem" width="100%" style={{ marginBottom: '0.5rem' }} />
          ))}
        </div>
      );
    }
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#9ca3af' }}></i>
      <h3>No Bids Found</h3>
      <p>
        {appliedFilters.search || appliedFilters.status.length > 0 || appliedFilters.analysts.length > 0 || appliedFilters.productTypes.length > 0
          ? 'No bids match your current filters. Try adjusting your search criteria.'
          : 'There are no bids pending review at the moment.'}
      </p>
    </div>
  );

  const renderComingSoon = () => (
    <div className={styles.comingSoonContainer}>
      <div className={styles.comingSoonContent}>
        <i className="pi pi-clock" style={{ fontSize: '3rem', color: '#522d6e' }}></i>
        <h3>Coming Soon</h3>
        <p>This feature is currently under development and will be available soon.</p>
      </div>
    </div>
  );

  // Get user name and role
  const userName = userMetadata?.userInfo?.name;
  const userRole = userMetadata?.userInfo?.deptId?.departmentName;
  const isLoadingUserInfo = !userMetadata?.userInfo;

  // Create analyst options with avatars
  const analystOptions = allAnalysts.map((analyst) => ({
    label: analyst,
    value: analyst,
  }));

  const assigneeOptionTemplate = (option: { label: string; value: string }) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Avatar
          label={getInitials(option.label)}
          shape="circle"
          size="normal"
          style={{
            backgroundColor: getBackgroundColor(option.label[0]),
            color: '#522D6E',
            width: '2rem',
            height: '2rem',
            fontSize: '0.75rem',
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <div className={styles.managerReviewContainer}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Button
          icon="pi pi-arrow-left"
          label="Back to Dashboard"
          className="p-button-text"
          onClick={() => navigate('/bdportal/bid-analysis/dashboard')}
        />
      </div>
      
      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabsWrapper}>
          <button
            className={`${styles.tab} ${activeTab === 'review-queue' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('review-queue')}
          >
            <i className="pi pi-list" style={{ marginRight: '0.5rem' }}></i>
            Review Queue
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'analytics' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('analytics')}
          >
            <i className="pi pi-chart-bar" style={{ marginRight: '0.5rem' }}></i>
            Analytics
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'history' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('history')}
          >
            <i className="pi pi-check-circle" style={{ marginRight: '0.5rem' }}></i>
            Approved Bids
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.contentContainer}>
        {activeTab === 'review-queue' ? (
          <>
            {/* User Profile Section */}
            <div className={styles.userProfileSection}>
              {isLoadingUserInfo ? (
                <>
                  <div className={styles.loadingAvatar}></div>
                  <div className={styles.userInfo}>
                    <h2 className={styles.userName}>Loading user info...</h2>
                  </div>
                </>
              ) : (
                <>
                  <Avatar
                    label={getInitials(userName || '')}
                    shape="circle"
                    size="large"
                    style={{
                      backgroundColor: '#e9d5ff',
                      color: '#522D6E',
                      width: '3.5rem',
                      height: '3.5rem',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                    }}
                  />
                  <div className={styles.userInfo}>
                    <h2 className={styles.userName}>
                      {userName}
                      {userRole && ` - ${userRole}`}
                    </h2>
                    {isLoadingAttributes ? (
                      <p className={styles.userAttributes}>Loading attributes...</p>
                    ) : userAttributes.length > 0 ? (
                      <p className={styles.userAttributes}>
                        {userAttributes.join(' • ')}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Filters and View Toggle */}
            <div className={styles.filtersRow}>
              <div className={styles.filterInputs}>
                <div className={styles.filterGroup}>
                  <label>Search</label>
                  <InputText
                    value={selectedFilters.search}
                    onChange={(e) => handleFilterChange({ ...selectedFilters, search: e.target.value })}
                    placeholder="Search by product name..."
                    className={styles.searchInput}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Status</label>
                  <MultiSelect
                    value={selectedFilters.status}
                    options={statusOptions}
                    onChange={(e) => handleFilterChange({ ...selectedFilters, status: e.value || [] })}
                    placeholder="All Statuses"
                    className={styles.filterDropdown}
                    display="chip"
                    maxSelectedLabels={2}
                    selectedItemsLabel="..."
                    showClear
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Analyst</label>
                  <MultiSelect
                    value={selectedFilters.analysts}
                    options={analystOptions}
                    onChange={(e) => handleFilterChange({ ...selectedFilters, analysts: e.value || [] })}
                    placeholder={isLoadingAnalysts ? "Loading analysts..." : "All Analysts"}
                    className={styles.filterDropdown}
                    display="chip"
                    maxSelectedLabels={2}
                    selectedItemsLabel="..."
                    showClear
                    filter
                    itemTemplate={assigneeOptionTemplate}
                    disabled={isLoadingAnalysts}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Product Type</label>
                  <MultiSelect
                    value={selectedFilters.productTypes}
                    options={allProductTypes.map((type) => ({ label: type, value: type }))}
                    onChange={(e) => handleFilterChange({ ...selectedFilters, productTypes: e.value || [] })}
                    placeholder="All Types"
                    className={styles.filterDropdown}
                    display="chip"
                    maxSelectedLabels={2}
                    selectedItemsLabel="..."
                    showClear
                  />
                </div>
              </div>

              <div className={styles.actionButtons}>
                <button 
                  className={styles.filterButton} 
                  onClick={handleApplyFilters}
                  title="Apply Filters"
                >
                  <Filter size={16} />
                </button>
              </div>
            </div>

            {/* View Toggle */}
            <div className={styles.viewToggleRow}>
              <div className={styles.viewToggleButtons}>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'cards' ? styles.active : ''}`}
                  onClick={() => viewMode === 'list' && handleViewModeToggle()}
                >
                  <LayoutGrid size={18} />
                </button>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                  onClick={() => viewMode === 'cards' && handleViewModeToggle()}
                >
                  <List size={18} />
                </button>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              renderLoadingSkeleton()
            ) : bids.length === 0 ? (
              renderEmptyState()
            ) : (
              <>
                {viewMode === 'cards' ? (
                  <div className={styles.cardsGrid}>
                    {bids.map((bid) => (
                      <BidCard key={bid.bid_id} bid={bid} onViewNotes={handleViewNotes} />
                    ))}
                  </div>
                ) : (
                  <BidListView bids={bids} onViewNotes={handleViewNotes} />
                )}

                {/* Pagination - Always show */}
                <div className={styles.paginationContainer}>
                  <Paginator
                    first={getFirstRecord()}
                    rows={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={handlePageChange}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    pageLinkSize={5}
                  />
                </div>
              </>
            )}
          </>
        ) : activeTab === 'analytics' ? (
          renderComingSoon()
        ) : activeTab === 'history' ? (
          <>
            {/* User Profile Section */}
            <div className={styles.userProfileSection}>
              {isLoadingUserInfo ? (
                <>
                  <div className={styles.loadingAvatar}></div>
                  <div className={styles.userInfo}>
                    <h2 className={styles.userName}>Loading user info...</h2>
                  </div>
                </>
              ) : (
                <>
                  <Avatar
                    label={getInitials(userName || '')}
                    shape="circle"
                    size="large"
                    style={{
                      backgroundColor: '#e9d5ff',
                      color: '#522D6E',
                      width: '3.5rem',
                      height: '3.5rem',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                    }}
                  />
                  <div className={styles.userInfo}>
                    <h2 className={styles.userName}>
                      {userName}
                      {userRole && ` - ${userRole}`}
                    </h2>
                    {isLoadingAttributes ? (
                      <p className={styles.userAttributes}>Loading attributes...</p>
                    ) : userAttributes.length > 0 ? (
                      <p className={styles.userAttributes}>
                        {userAttributes.join(' • ')}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            {/* Filters and View Toggle */}
            <div className={styles.filtersRow}>
              <div className={styles.filterInputs}>
                <div className={styles.filterGroup}>
                  <label>Analyst</label>
                  <MultiSelect
                    value={selectedApprovedBidsFilters.analysts}
                    options={approvedBidsAllAnalysts.map((analyst) => ({ label: analyst, value: analyst }))}
                    onChange={(e) => handleApprovedBidsFilterChange({ ...selectedApprovedBidsFilters, analysts: e.value || [] })}
                    placeholder={isLoadingApprovedBidsAnalysts ? "Loading analysts..." : "All Analysts"}
                    className={styles.filterDropdown}
                    display="chip"
                    maxSelectedLabels={2}
                    selectedItemsLabel="..."
                    showClear
                    filter
                    itemTemplate={assigneeOptionTemplate}
                    disabled={isLoadingApprovedBidsAnalysts}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Product Type</label>
                  <MultiSelect
                    value={selectedApprovedBidsFilters.productTypes}
                    options={allProductTypes.map((type) => ({ label: type, value: type }))}
                    onChange={(e) => handleApprovedBidsFilterChange({ ...selectedApprovedBidsFilters, productTypes: e.value || [] })}
                    placeholder="All Types"
                    className={styles.filterDropdown}
                    display="chip"
                    maxSelectedLabels={2}
                    selectedItemsLabel="..."
                    showClear
                  />
                </div>
              </div>

              <div className={styles.actionButtons}>
                <button 
                  className={styles.filterButton} 
                  onClick={handleApprovedBidsApplyFilters}
                  title="Apply Filters"
                >
                  <Filter size={16} />
                </button>
              </div>
            </div>

            {/* View Toggle */}
            <div className={styles.viewToggleRow}>
              <div className={styles.viewToggleButtons}>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'cards' ? styles.active : ''}`}
                  onClick={() => viewMode === 'list' && handleViewModeToggle()}
                >
                  <LayoutGrid size={18} />
                </button>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                  onClick={() => viewMode === 'cards' && handleViewModeToggle()}
                >
                  <List size={18} />
                </button>
              </div>
            </div>

            {/* Content */}
            {approvedBidsLoading ? (
              renderLoadingSkeleton()
            ) : approvedBids.length === 0 ? (
              <div className={styles.emptyState}>
                <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#9ca3af' }}></i>
                <h3>No Approved Bids Found</h3>
                <p>
                  {appliedApprovedBidsFilters.analysts.length > 0 || appliedApprovedBidsFilters.productTypes.length > 0
                    ? 'No approved bids match your current filters. Try adjusting your filter criteria.'
                    : 'There are no approved bids at the moment.'}
                </p>
              </div>
            ) : (
              <>
                {viewMode === 'cards' ? (
                  <div className={styles.cardsGrid}>
                    {approvedBids.map((bid) => (
                      <BidCard key={bid.bid_id} bid={bid} onViewNotes={handleViewNotes} viewOnly={true} />
                    ))}
                  </div>
                ) : (
                  <BidListView bids={approvedBids} onViewNotes={handleViewNotes} viewOnly={true} />
                )}

                {/* Pagination - Always show */}
                <div className={styles.paginationContainer}>
                  <Paginator
                    first={getApprovedBidsFirstRecord()}
                    rows={pageSize}
                    totalRecords={approvedBidsTotalRecords}
                    onPageChange={handleApprovedBidsPageChange}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    pageLinkSize={5}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          renderComingSoon()
        )}
      </div>

      {/* Notes Modal */}
      {selectedBidForNotes && (
        <NotesViewModal
          visible={isNotesModalVisible}
          onHide={handleCloseNotesModal}
          notes={selectedBidForNotes.notes}
          moleculeName={selectedBidForNotes.molecule_name}
        />
      )}
    </div>
  );
};

export default observer(ManagerReview);

